// providers/PlayerProvider.tsx
import {
  useState, useCallback, useRef, ReactNode, useEffect,
} from 'react'
import {
  PlayerContext, PlayerContextValue,
  PLAYER_STORAGE_KEY, GameId, apiSubmitScore,
} from '../hooks/usePlayer'

// ─── Username Gate ─────────────────────────────────────────────────────────────
// Matches your existing dark/pink Tailwind aesthetic.
// Uses dvh so it works on iOS Safari without the address-bar gap.

const HANDLE_RE = /^[A-Za-z0-9_]{1,15}$/

function UsernameGate({ onConfirm }: { onConfirm: (u: string) => void }) {
  const [value, setValue]     = useState('')
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)
  const [shake, setShake]     = useState(false)
  const inputRef              = useRef<HTMLInputElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const confirm = async () => {
    const handle = value.trim().replace(/^@/, '')
    if (!handle) {
      setError('Enter your X handle to continue.')
      triggerShake()
      return
    }
    if (!HANDLE_RE.test(handle)) {
      setError('Handles are letters, numbers and _ only (max 15 chars).')
      triggerShake()
      return
    }
    setLoading(true)
    // Small artificial delay so the button feels responsive
    await new Promise(r => setTimeout(r, 150))
    onConfirm(handle)
  }

  const triggerShake = () => {
    setShake(true)
    setTimeout(() => setShake(false), 450)
  }

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center bg-[#0a0008] px-4">
      {/* Scanlines */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.07) 2px,rgba(0,0,0,0.07) 4px)',
        }}
      />

      <div
        className={[
          'relative z-10 w-full max-w-sm',
          'border border-pink-900/40 rounded-2xl',
          'bg-[#12000f]/80 backdrop-blur-xl',
          'p-8 sm:p-10 text-center',
          'transition-transform',
          shake ? 'animate-[shake_0.4s_ease]' : '',
        ].join(' ')}
      >
        {/* Logo mark */}
        <div className="mx-auto mb-6 w-14 h-14 rounded-2xl bg-pink-600 flex items-center justify-center shadow-[0_0_32px_rgba(236,72,153,0.4)]">
          <span className="font-display text-white text-3xl leading-none select-none">S</span>
        </div>

        <p className="font-mono text-[10px] tracking-[4px] text-pink-400/70 uppercase mb-1">
          Parrot Games
        </p>
        <h1 className="font-display text-2xl text-white tracking-widest mb-2">
          10K SQUAD
        </h1>
        <p className="font-mono text-xs text-pink-200/40 tracking-widest mb-8">
          Enter your X handle to play + join the leaderboard
        </p>

        {/* Input */}
        <div
          className={[
            'flex items-center',
            'border rounded-xl overflow-hidden mb-3',
            error
              ? 'border-pink-500/70 bg-pink-950/30'
              : 'border-pink-900/50 bg-[#0a0008]/60',
            'focus-within:border-pink-500 transition-colors',
          ].join(' ')}
        >
          <span className="pl-4 pr-1 font-mono text-pink-500/60 text-base select-none">@</span>
          <input
            ref={inputRef}
            type="text"
            inputMode="text"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            maxLength={15}
            placeholder="yourhandle"
            value={value}
            onChange={e => { setValue(e.target.value); setError('') }}
            onKeyDown={e => e.key === 'Enter' && confirm()}
            className={[
              'flex-1 min-w-0 bg-transparent py-3 pr-4',
              'font-mono text-sm text-white placeholder-pink-900/60',
              'outline-none',
            ].join(' ')}
          />
        </div>

        {/* Error */}
        <div className="min-h-[18px] mb-3">
          {error && (
            <p className="font-mono text-[10px] text-pink-400 tracking-wide text-left">
              {error}
            </p>
          )}
        </div>

        {/* CTA */}
        <button
          onClick={confirm}
          disabled={loading}
          className={[
            'w-full py-3 rounded-xl',
            'bg-pink-600 hover:bg-pink-500 active:scale-[0.98]',
            'font-mono text-xs text-white tracking-[3px] uppercase',
            'transition-all duration-150',
            'shadow-[0_0_24px_rgba(236,72,153,0.3)]',
            'disabled:opacity-60 disabled:cursor-not-allowed',
          ].join(' ')}
        >
          {loading ? 'One sec…' : "Let's play →"}
        </button>

        <p className="mt-6 font-mono text-[9px] text-pink-900/80 tracking-wide leading-relaxed">
          Your handle is saved in your browser and only used to show your score on the leaderboard.
        </p>
      </div>

      {/* Keyframe for shake — injected once */}
      <style>{`
        @keyframes shake {
          0%,100%{ transform:translateX(0) }
          20%{ transform:translateX(-7px) }
          40%{ transform:translateX(7px) }
          60%{ transform:translateX(-4px) }
          80%{ transform:translateX(4px) }
        }
      `}</style>
    </div>
  )
}

// ─── Provider ──────────────────────────────────────────────────────────────────

export default function PlayerProvider({ children }: { children: ReactNode }) {
  const [username, setUsernameState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    try { return localStorage.getItem(PLAYER_STORAGE_KEY) } catch { return null }
  })

  // Dedup guard: track (game, score, bucket) already submitted this session
  const submittedRef = useRef<Set<string>>(new Set())

  const setUsername = useCallback((u: string) => {
    try { localStorage.setItem(PLAYER_STORAGE_KEY, u) } catch {}
    setUsernameState(u)
  }, [])

  const clearUsername = useCallback(() => {
    try { localStorage.removeItem(PLAYER_STORAGE_KEY) } catch {}
    setUsernameState(null)
    submittedRef.current.clear()
  }, [])

  const submitScore = useCallback(
    async (game: GameId, score: number): Promise<boolean> => {
      if (!username || score <= 0) return false

      // Deduplicate: same game + same score within a 5-second window
      const bucket = Math.floor(Date.now() / 5_000)
      const key    = `${game}:${score}:${bucket}`
      if (submittedRef.current.has(key)) return false
      submittedRef.current.add(key)

      try {
        const result = await apiSubmitScore(game, username, score)
        return result.updated
      } catch (err) {
        console.error('[submitScore]', err)
        // Remove from dedup set so a retry is possible
        submittedRef.current.delete(key)
        return false
      }
    },
    [username],
  )

  const value: PlayerContextValue = { username, setUsername, clearUsername, submitScore }

  if (!username) {
    return <UsernameGate onConfirm={setUsername} />
  }

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  )
}